import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../config";
import { Star, User, MessageSquare } from "lucide-react";
import ReviewForm from "./ReviewForm";

const ReviewsSection = ({ userId, productId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canReview, setCanReview] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      console.log("Fetching reviews for productId:", productId);
      const response = await fetch(`${API_BASE_URL}/api/reviews/product/${productId}`);
      console.log("Reviews response status:", response.status);
      if (!response.ok) throw new Error("Failed to fetch reviews");
      const data = await response.json();
      console.log("Reviews data:", data);
      setReviews(data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const checkCanReview = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(
        `${API_BASE_URL}/api/reviews/can-review?productId=${productId}`,
        { headers: { Authorization: token } }
      );
      if (response.ok) {
        const canReviewResult = await response.json();
        // Backend returns boolean directly
        setCanReview(canReviewResult === true);
      }
    } catch (error) {
      console.error("Error checking review eligibility:", error);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
    checkCanReview();
  }, [fetchReviews, checkCanReview]);

  const handleReviewSubmitted = () => {
    fetchReviews();
    setCanReview(false);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? "text-yellow-400 fill-current" : "text-gray-300"}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-gray-100 rounded-lg p-6 animate-pulse">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Review Form */}
      {canReview && (
        <ReviewForm productId={productId} onReviewSubmitted={handleReviewSubmitted} />
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">ยังไม่มีรีวิว</h3>
          <p className="text-gray-500">เป็นคนแรกที่จะรีวิวสินค้านี้</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div
              key={review.reviewId}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex gap-4">
                {/* Avatar */}
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  {review.accountName?.charAt(0)?.toUpperCase() || <User size={20} />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <h4 className="font-medium text-gray-900">{review.accountName || "ผู้ใช้งาน"}</h4>
                      <p className="text-sm text-gray-500">{formatDate(review.createdAt)}</p>
                    </div>
                    {renderStars(review.rating)}
                  </div>

                  <p className="text-gray-600 leading-relaxed">{review.comment}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
